import { useCallback, useEffect, useRef, useState } from 'react';
import PancakeTower, { PancakeTowerResult } from '../games/pancake-tower';
import PancakeLeaderboard from './PancakeLeaderboard';
import { getMyScores, hasGameAccount, startGameSession, submitScore, GameSession } from '../api/game.api';

type Note =
  | { kind: 'offline' }
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; todayRank: number | null; newBest: boolean; requiresReview: boolean }
  | { kind: 'failed' };

/**
 * Pancake Tower with score saving: one POST at start and one at game over, a small result note and the
 * leaderboard underneath. Saving problems never block replay or leave the page.
 */
export default function PancakeArena() {
  const [account] = useState(hasGameAccount);
  const [note, setNote] = useState<Note>(account ? { kind: 'idle' } : { kind: 'offline' });
  const [refreshKey, setRefreshKey] = useState(0);
  const requiresReview = note.kind === 'saved' && note.requiresReview;
  useEffect(() => {
    if (!requiresReview) return;
    const timer = window.setTimeout(() => setNote(current => current.kind === 'saved' ? { ...current, requiresReview: false } : current), 5000);
    return () => window.clearTimeout(timer);
  }, [requiresReview, refreshKey]);
  /** personal all-time best: undefined = unknown (not loaded / failed), null = never played */
  const bestRef = useRef<number | null | undefined>(undefined);
  const roundRef = useRef(0);
  const sessionRef = useRef<Promise<GameSession | null> | null>(null);

  const onGameStart = useCallback(() => {
    ++roundRef.current;
    setNote(hasGameAccount() ? { kind: 'idle' } : { kind: 'offline' });
    // Do not await: frame/tap processing remains entirely local, even offline.
    sessionRef.current = hasGameAccount() ? startGameSession().catch(() => null) : null;
  }, []);

  useEffect(() => {
    if (!account) return;
    let alive = true;
    getMyScores()
      .then((me) => { if (alive && bestRef.current === undefined) bestRef.current = me?.best ?? null; })
      .catch(() => { /* personal best is optional */ });
    return () => { alive = false; };
  }, [account]);

  const onGameOver = useCallback((result: PancakeTowerResult) => {
    if (!hasGameAccount()) {
      setNote({ kind: 'offline' });
      return;
    }
    if (!(result.score > 0)) {
      setNote({ kind: 'idle' });
      return;
    }
    const round = roundRef.current;
    const session = sessionRef.current;
    sessionRef.current = null; // one result per local round, too
    setNote({ kind: 'saving' });
    Promise.resolve(session)
      .then((s) => {
        if (!s) throw new Error('Game session unavailable');
        return submitScore(result, s.sessionId);
      })
      .then((res) => {
        const prior = bestRef.current;
        const returnedBest = res?.best?.all ?? null;
        const newBest = prior === undefined ? returnedBest === result.score : prior === null || result.score > prior;
        bestRef.current = Math.max(prior ?? 0, returnedBest ?? 0, result.score);
        if (round !== roundRef.current) return;
        setNote({ kind: 'saved', todayRank: res?.rank?.today ?? null, newBest, requiresReview: res?.moderationStatus === 'review' });
        setRefreshKey((k) => k + 1);
      })
      .catch(() => {
        if (round === roundRef.current) setNote({ kind: 'failed' });
      });
  }, []);

  return (
    <div className="w-full">
      <PancakeTower onGameStart={onGameStart} onGameOver={onGameOver} />
      <p role="status" aria-live="polite" className="mx-auto mt-3 flex min-h-[28px] max-w-[460px] flex-wrap items-center justify-center gap-2 px-2 text-center text-sm">
        <NoteText note={note} />
      </p>
      <PancakeLeaderboard refreshKey={refreshKey} />
    </div>
  );
}

function NoteText({ note }: { note: Note }) {
  const pill = 'inline-flex items-center rounded-full px-3 py-1 font-bold';
  switch (note.kind) {
    case 'offline':
      return <span className="text-[#7a5b48]">โหมดออฟไลน์ · เข้าสู่ระบบเพื่อบันทึกคะแนน</span>;
    case 'saving':
      return <span className="text-[#7a5b48]">กำลังบันทึกคะแนน…</span>;
    case 'failed':
      return <span className="text-[#7a5b48]">บันทึกคะแนนไม่สำเร็จ — เล่นต่อได้เลย</span>;
    case 'saved':
      return (
        <>
          {note.requiresReview && <span className={`${pill} bg-[#f4e9da] text-[#3b2a20]`}>คะแนนรอบนี้รอตรวจสอบ</span>}
          {note.newBest && <span className={`${pill} bg-[#df7836] text-white`}>สถิติใหม่!</span>}
          <span className={`${pill} bg-[#f4e9da] text-[#3b2a20]`}>
            {note.todayRank != null ? `อันดับวันนี้ #${note.todayRank}` : 'บันทึกคะแนนแล้ว'}
          </span>
        </>
      );
    default:
      return null;
  }
}
